import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import type { MantineFormValues } from "@/Components/NextBase/shared.types";
import type { GetPaymentForOverview } from "@/server-types";
import { Divider, Link } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { returnCurrencySymbol } from "../../../../../shared/utils/utils";

export function AppointmentRefundFields({
  payment,
  isLoadingPayment,
  userAppointmentId,
  shouldRefundPaymentProps,
  refundAmountProps,
  refundReasonProps,
}: {
  payment: GetPaymentForOverview["response"] | undefined;
  isLoadingPayment: boolean;
  userAppointmentId?: number;
  shouldRefundPaymentProps: MantineFormValues;
  refundAmountProps: MantineFormValues;
  refundReasonProps: MantineFormValues;
}) {
  if (payment == null || !payment.isRefundable) {
    return null;
  }

  const isRefundEnabled: boolean = shouldRefundPaymentProps.checked;
  const refundAmount: number = refundAmountProps.value;

  return (
    <div className="w-full">
      <Divider className="mb-2" />

      <p className="mb-2 text-sm font-medium">
        <Trans>Spletno plačilo</Trans>
      </p>

      <LimeSwitch
        title={t`Vrni spletno plačilo`}
        {...shouldRefundPaymentProps}
        isCompact
        className="mb-2"
      />

      <LimeCollapse isOpen={isRefundEnabled}>
        <p className="text-sm text-gray-500">
          <Trans>Stranka je plačala</Trans> {payment.amountFormatted}
        </p>

        <Link
          href={`/dashboard/stripe/payments?uid=${userAppointmentId}`}
          showAnchorIcon
          color="primary"
          size="sm"
          target="_blank"
          rel="noopener noreferrer"
        >
          <p className="text-sm">
            <Trans>Odpri podrobnosti o plačilu</Trans>
          </p>
        </Link>

        <LimeNumberInput
          label={t`Znesek za vračilo`}
          size="sm"
          {...refundAmountProps}
          maxValue={payment.refundableAmountCents / 100 || 0}
          minValue={0.01}
          isDisabled={isLoadingPayment}
          step={0.01}
          className="my-2"
          endContent={
            <div className="text-sm text-gray-500">
              {returnCurrencySymbol({ currency: payment.currency || "EUR" })}
            </div>
          }
        />

        <LimeSelect
          size="sm"
          label={t`Razlog za vračilo`}
          items={[
            {
              label: t`Zahtevano s strani stranke`,
              key: "requested_by_customer",
            },
            { label: t`Podvojeno plačilo`, key: "duplicate" },
            { label: t`Zloraba`, key: "fraudulent" },
          ]}
          {...refundReasonProps}
          isLoading={isLoadingPayment}
        />

        {refundAmount > payment.maxNoLossRefundAmountCents / 100 && (
          <p className="text-error mt-2 text-xs">
            <Trans>
              Razlika med zneskom vračila in maksimalnim zneskom, ki ga lahko
              vrnete brez izgube ({payment.maxNoLossRefundAmountCentsFormatted}
              ), bo vzeta iz vašega računa.
            </Trans>
          </p>
        )}
      </LimeCollapse>
    </div>
  );
}
